import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed } from '@angular/core';
import { CalendarAttendee } from '@maximizer/outlook/shared/domain';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { attendeesNotInMaximizer } from '../utils/appointment.util';

export type { CalendarAttendee };

type CalendarState = {
  isLoading: boolean;
  attendees: CalendarAttendee[];
  savedAppointmentKey: string | null;
};

const initialState: CalendarState = {
  isLoading: false,
  attendees: [],
  savedAppointmentKey: null,
};

export const CalendarStore = signalStore(
  withDevtools('calendar'),
  withState(initialState),
  withComputed((store) => ({
    hasAttendees: computed<boolean>(() => store.attendees().length > 0),
    isAppointmentSaved: computed<boolean>(
      () => store.savedAppointmentKey() !== null,
    ),
    inMaximizerAttendees: computed<CalendarAttendee[]>(() =>
      store.attendees().filter((item) => item.isInMaximizer),
    ),
    notInMaximizerAttendees: computed<CalendarAttendee[]>(() =>
      attendeesNotInMaximizer(store.attendees()),
    ),
    requiredAttendees: computed<CalendarAttendee[]>(() =>
      store.attendees().filter((item) => item.isInMaximizer && item.isRequired),
    ),
    optionalAttendees: computed<CalendarAttendee[]>(() =>
      store
        .attendees()
        .filter((item) => item.isInMaximizer && !item.isRequired),
    ),
  })),
  withMethods((store) => ({
    setLoading(isLoading: boolean) {
      patchState(store, () => ({ isLoading }));
    },
    setSavedAppointmentKey(savedAppointmentKey: string | null) {
      patchState(store, () => ({ savedAppointmentKey }));
    },
    updateAttendees(attendees: CalendarAttendee[]) {
      patchState(store, () => ({ attendees }));
    },
    addAttendee(attendee: CalendarAttendee) {
      const current = store.attendees();
      const exists = current.some(
        (item) =>
          item.emailAddress === attendee.emailAddress &&
          item.isRequired === attendee.isRequired,
      );
      if (!exists) {
        patchState(store, () => ({ attendees: [...current, attendee] }));
      }
    },
    removeAttendee(emailAddress: string) {
      const updated = store
        .attendees()
        .filter((item) => item.emailAddress !== emailAddress);
      patchState(store, () => ({ attendees: updated }));
    },
  })),
);

export type CalendarStore = InstanceType<typeof CalendarStore>;
